import type { ActivityMessage } from "@/api/models"
import type { TFunction } from "i18next"

import { formatFriendlyDateTime, isDateTimeString } from "@/lib/dates"
import { logger } from "@/lib/logger"

function formatDateTimeValues(
  details: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!details) return details

  const formatted: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(details)) {
    if (typeof value === "string" && isDateTimeString(value)) {
      formatted[key] = formatFriendlyDateTime(value)
    } else {
      formatted[key] = value
    }
  }
  return formatted
}

/** Argument summary of a `hosted_tool_call`: its preview, else its action, else "". */
function hostedToolCallSummary(details: Record<string, unknown> | undefined): string {
  const preview = typeof details?.preview === "string" ? details.preview.trim() : ""
  if (preview) return preview
  return typeof details?.action === "string" ? details.action.trim() : ""
}

/**
 * Translates activity messages using i18next.
 *
 * If the activity message has a translation_key in its details, it will be used
 * to look up the translation in the "activity_messages" namespace.
 *
 * The function will replace placeholders in the translation with values from the message's details.
 * If the translated text contains placeholders that refer to other translation keys, it will
 * recursively resolve them.
 */
export function translateActivityMessage(activityMessage: ActivityMessage, t: TFunction): string {
  if (!activityMessage.message) {
    return ""
  }

  // Get translation key, defaulting to activity_type if not present in details
  const translationKey = activityMessage.details?.translation_key || activityMessage.activity_type

  // If we have a translation key, use it to translate
  if (typeof translationKey === "string") {
    const key = `activity_messages.${translationKey}`

    // Format any datetime values in details before passing to i18next
    const formattedDetails = formatDateTimeValues(activityMessage.details)

    // Pre-translate tool/tool_name to display names before i18next interpolation.
    // Without this, i18next replaces {{tool}} with the raw value (e.g. "library_search")
    // before resolveNestedPlaceholders can translate it via tool_<name> lookup.
    const detailsForInterpolation = { ...formattedDetails }
    if (typeof detailsForInterpolation.tool === "string") {
      const toolTranslation = t(`activity_messages.tool_${detailsForInterpolation.tool}`, {
        defaultValue: "",
      })
      if (toolTranslation) {
        detailsForInterpolation.tool = toolTranslation
      }
    }
    if (typeof detailsForInterpolation.tool_name === "string") {
      const toolTranslation = t(`activity_messages.tool_${detailsForInterpolation.tool_name}`, {
        defaultValue: "",
      })
      if (toolTranslation) {
        detailsForInterpolation.tool_name = toolTranslation
      }
    }
    // Pre-translate Deep Explorer subagent role (worker / scout / fact_checker
    // / deep_explorer) into a localized noun so templates can render
    // "[Worker] using filskrivning" instead of "[worker#abc123] ...". Mirrors
    // the tool_<name> humanization above; falls back to the raw value if
    // no role_<value> key is present.
    if (typeof detailsForInterpolation.subagent_role === "string") {
      const roleTranslation = t(`activity_messages.role_${detailsForInterpolation.subagent_role}`, {
        defaultValue: "",
      })
      if (roleTranslation) {
        detailsForInterpolation.subagent_role = roleTranslation
      }
    }

    // A hosted tool call with no argument summary (e.g. `memory_notes`) used
    // to render "memory_notes: " — and the activity rail's bold wrap then
    // showed the literal "**memory_notes: **". The action stands in for a
    // missing preview; with neither, the label is the tool name alone.
    if (translationKey === "hosted_tool_call") {
      const summary = hostedToolCallSummary(activityMessage.details)
      if (!summary) {
        const tool = detailsForInterpolation.tool
        return typeof tool === "string" && tool.trim()
          ? tool.trim()
          : activityMessage.message.trim()
      }
      detailsForInterpolation.preview = summary
    }

    // Translate the main message
    const translated = t(key, {
      ...detailsForInterpolation,
      defaultValue: activityMessage.message,
    })

    // Resolve any nested placeholders in the translated text
    let result = resolveNestedPlaceholders({
      text: translated,
      details: activityMessage.details,
      t,
      maxDepth: 4,
    })

    // Final safety net: strip any {{placeholder}} that still survived both
    // i18next's first-pass interpolation and the nested-placeholder resolver.
    // An unresolved `{{maxAttempts}}` leaking into the UI looks broken; it's
    // better to drop the token (along with its adjacent slash/space) than
    // render raw template syntax.
    if (result.includes("{{")) {
      result = result
        .replace(/\s*\/?\s*{{[^}]+}}/g, "")
        .replace(/\s{2,}/g, " ")
        .trim()
    }

    return result
  }

  // Fallback to the original message
  return activityMessage.message
}

/**
 * Options for resolving nested placeholders
 */
interface ResolveOptions {
  text: string
  details: Record<string, unknown> | undefined
  t: TFunction
  maxDepth?: number
}

/**
 * Resolves any {{placeholder}} in the text by checking if they're in the details object
 * or if they exist as separate translation keys.
 *
 * @param options The options for resolving placeholders
 * @returns The text with resolved placeholders
 */
function resolveNestedPlaceholders(options: ResolveOptions): string {
  const { text, details, t, maxDepth = 4 } = options

  // Base cases
  if (!details) {
    return text
  }
  if (maxDepth <= 0) {
    return text
  }
  if (!text.includes("{{")) {
    return text
  }

  // Find all placeholders like {{key}}
  const placeholderRegex = /{{([^,}]+)}}/g
  let result = text
  let match

  // Reset regex to start from beginning of string
  placeholderRegex.lastIndex = 0

  // For each placeholder found
  while ((match = placeholderRegex.exec(result)) !== null) {
    const fullMatch = match[0] // The entire {{key}} part
    const placeholderKey = match[1].trim() // Just the key inside, trimmed

    // If the key exists in details, replace with its value
    if (details[placeholderKey] !== undefined) {
      const resolved = String(details[placeholderKey])

      result = result.replace(fullMatch, resolved)

      // Reset regex to continue search from this position
      placeholderRegex.lastIndex = 0
      continue
    }

    // Try to translate the placeholder as a nested key
    const nestedTranslationKey = `activity_messages.${placeholderKey}`

    try {
      // Create a new options object with relevant parts of the original details
      // Especially important to keep count for pluralization
      const nestedDetails = {
        ...details,
      }

      const nestedValue = t(nestedTranslationKey, {
        ...nestedDetails,
        defaultValue: "",
      })

      // Only replace if we got a non-empty translation
      if (nestedValue) {
        // Recursively resolve any placeholders in the nested value
        let resolvedNestedValue = nestedValue
        if (nestedValue.includes("{{")) {
          resolvedNestedValue = resolveNestedPlaceholders({
            text: nestedValue,
            details: nestedDetails,
            t,
            maxDepth: maxDepth - 1,
          })
        }

        // Replace in the result
        result = result.replace(fullMatch, resolvedNestedValue)

        // Reset regex to catch any new placeholders introduced by the replacement
        placeholderRegex.lastIndex = 0
      }
    } catch (e) {
      // If translation fails, keep the original placeholder
      logger.warn(`Activity message translator: Failed to resolve placeholder:`, e as Error)
    }
  }

  return result
}

export type ActivityGroupTitleInput = ActivityMessage["details"] extends infer D
  ? D extends { group_title?: infer G }
    ? G
    : never
  : never

const GROUP_TITLE_KEY = "group_title_research_depth"

export function decodeGroupTitle(
  groupTitle: ActivityGroupTitleInput | undefined
): { key: string; depth?: number } | undefined {
  if (!groupTitle) {
    return undefined
  }

  if (typeof groupTitle === "string") {
    if (groupTitle.startsWith("group_title_research_depth|")) {
      const [, rawDepth] = groupTitle.split("|")
      const depth = rawDepth ? Number(rawDepth) : undefined
      return { key: GROUP_TITLE_KEY, depth: Number.isFinite(depth) ? depth : undefined }
    }

    return { key: groupTitle }
  }

  if (
    typeof groupTitle === "object" &&
    groupTitle !== null /* eslint-disable-line @typescript-eslint/no-unnecessary-condition */ &&
    "translation_key" in groupTitle
  ) {
    const record = groupTitle as Record<string, unknown>
    const translationKey =
      typeof record.translation_key === "string" ? record.translation_key : undefined
    const depthValue =
      "depth" in record && typeof record.depth === "number" ? (record.depth as number) : undefined

    if (typeof translationKey === "string") {
      return {
        key: translationKey,
        depth: depthValue,
      }
    }
  }

  return undefined
}

export function translateActivityGroupTitle(
  groupTitle: ActivityGroupTitleInput | undefined,
  t: TFunction
): string | undefined {
  if (!groupTitle) {
    return undefined
  }

  const decoded = decodeGroupTitle(groupTitle)
  if (!decoded) {
    return undefined
  }

  const translationPath = decoded.key.startsWith("activity_messages.")
    ? decoded.key
    : `activity_messages.${decoded.key}`
  const fallback = decoded.key

  const translation =
    decoded.depth !== undefined
      ? t(translationPath, { depth: decoded.depth, defaultValue: fallback })
      : t(translationPath, { defaultValue: fallback })

  return translation || fallback
}
